import type { GameState } from './types';
import { AGES, BALANCE, FINAL_AGE_INDEX, TECHS_BY_ID, getAge } from './content';
import { collectModifiers } from './modifiers';
import { beginGeneration } from './state';

export interface PrestigeQuote {
  /** Ancestral Knowledge that would be gained right now. */
  knowledge: number;
  /** Whether the fire may be passed at all. */
  canPass: boolean;
  /** Why it cannot be passed, when it cannot. */
  blockedReason: string | null;
  peakPopulation: number;
  techsKnown: number;
}

export interface AgeQuote {
  canAdvance: boolean;
  blockedReason: string | null;
  generationsDone: number;
  generationsRequired: number;
  capstoneTech: string;
  hasCapstone: boolean;
  isFinalAge: boolean;
}

/** Ancestral Knowledge the tribe would carry forward if the fire passed now. */
export function knowledgeOnReset(state: GameState): number {
  const modifiers = collectModifiers(state);
  const populationTerm = Math.pow(
    Math.max(0, state.peakPopulation),
    BALANCE.akPopulationExponent,
  );
  const techTerm = 1 + state.techs.length * BALANCE.akPerTech;
  const ageTerm = Math.pow(BALANCE.akPerAge, state.ageIndex);
  const raw =
    BALANCE.akCoefficient * populationTerm * techTerm * ageTerm * modifiers.akGain;
  return Math.floor(Math.max(0, raw));
}

export function quotePrestige(state: GameState): PrestigeQuote {
  const knowledge = knowledgeOnReset(state);
  const meetsPopulation =
    state.peakPopulation >= BALANCE.minimumPeakPopulationToPrestige;

  return {
    knowledge,
    canPass: meetsPopulation && knowledge > 0,
    blockedReason: meetsPopulation
      ? knowledge > 0
        ? null
        : 'The line has learned nothing worth carrying.'
      : `The tribe must reach ${BALANCE.minimumPeakPopulationToPrestige} members before the fire can be passed.`,
    peakPopulation: state.peakPopulation,
    techsKnown: state.techs.length,
  };
}

/**
 * Layer one prestige. Bank Ancestral Knowledge and start a new generation
 * inside the same age.
 */
export function passTheFire(state: GameState): number {
  const quote = quotePrestige(state);
  if (!quote.canPass) return 0;

  const gained = quote.knowledge;
  const next = beginGeneration(state);

  Object.assign(state, next);
  state.ancestralKnowledge += gained;
  state.generation += 1;
  state.generationsInAge += 1;
  state.stats.totalGenerations += 1;
  state.stats.bestGenerationAk = Math.max(state.stats.bestGenerationAk, gained);

  return gained;
}

export function quoteAge(state: GameState): AgeQuote {
  const age = getAge(state.ageIndex);
  const hasCapstone = state.techs.includes(age.capstoneTech);
  const generationsDone = state.generationsInAge;
  const enoughGenerations = generationsDone >= age.generationsRequired;
  const isFinalAge = state.ageIndex >= FINAL_AGE_INDEX;

  const capstoneName = TECHS_BY_ID[age.capstoneTech]?.name ?? age.capstoneTech;

  let blockedReason: string | null = null;
  if (!hasCapstone) {
    blockedReason = `The tribe has not yet discovered ${capstoneName}.`;
  } else if (!enoughGenerations) {
    const remaining = age.generationsRequired - generationsDone;
    blockedReason = `${remaining} more generation${remaining === 1 ? '' : 's'} must pass.`;
  }

  return {
    canAdvance: hasCapstone && enoughGenerations,
    blockedReason,
    generationsDone,
    generationsRequired: age.generationsRequired,
    capstoneTech: age.capstoneTech,
    hasCapstone,
    isFinalAge,
  };
}

/**
 * Layer two prestige. Banks knowledge like a generation reset, then moves the
 * tribe into the next age. On the final age this instead founds the settlement.
 */
export function advanceAge(state: GameState): { knowledge: number; newAgeIndex: number } {
  const ageQuote = quoteAge(state);
  if (!ageQuote.canAdvance) return { knowledge: 0, newAgeIndex: state.ageIndex };

  const gained = knowledgeOnReset(state);

  if (ageQuote.isFinalAge) {
    // The end of the primal era. The run is marked complete and the tribe
    // continues, so there is still something to do afterwards.
    state.completedAt = state.stats.totalPlaySeconds;
    state.rites += 1;
    const next = beginGeneration(state);
    Object.assign(state, next);
    state.ancestralKnowledge += gained;
    state.generation += 1;
    state.generationsInAge += 1;
    state.stats.totalGenerations += 1;
    return { knowledge: gained, newAgeIndex: state.ageIndex };
  }

  const next = beginGeneration(state);
  Object.assign(state, next);
  state.ancestralKnowledge += gained;
  state.generation += 1;
  state.stats.totalGenerations += 1;
  state.stats.bestGenerationAk = Math.max(state.stats.bestGenerationAk, gained);
  state.ageIndex = Math.min(FINAL_AGE_INDEX, state.ageIndex + 1);
  state.generationsInAge = 0;
  state.rites += 1;

  // A new age begins with the head start its ancestral nodes allow, which may
  // include capstones from earlier ages.
  return { knowledge: gained, newAgeIndex: state.ageIndex };
}

/** Progress through the whole game, 0 to 1, for the header display. */
export function overallProgress(state: GameState): number {
  const perAge = 1 / AGES.length;
  const age = getAge(state.ageIndex);
  const withinAge = Math.min(1, state.generationsInAge / age.generationsRequired);
  return Math.min(1, state.ageIndex * perAge + withinAge * perAge);
}
